'use client';
import { cn } from '@/lib/utils';
import { getCookie, getCookies, hasCookie } from 'cookies-next';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ElementType, PropsWithChildren, useEffect, useState } from 'react';

import BalloonIcon from '../Icons/Admin/Balloon';
import CalendarBookingsIcon from '../Icons/Admin/CalendarBookings';
import DashboardIcon from '../Icons/Admin/Dashboard';
import DialogBoxIcon from '../Icons/Admin/DialogBox';
import DocumentsIcon from '../Icons/Admin/Documents';
import FlagIcon from '../Icons/Admin/Flag';
import PowerIcon from '../Icons/Admin/Power';
import ProfileIcon from '../Icons/Admin/Profile';
import VillageIcon from '../Icons/Admin/Village';
import WalletIcon from '../Icons/Admin/Wallet';

import useClickOutside from '@/hooks/useClickOutside';
import useUserContext from '@/hooks/useUserContext';

import { IUserDropDownDictionary } from '@/dictionaries/components/userDropDown/types';
import { DEV_URL } from '@/utils/url';
import dropdownPaths from './dropdown-paths';

type Props = {
  dict: IUserDropDownDictionary;
} & PropsWithChildren;

type ListItemProps = {
  label: string;
  href: string;
  icon: ElementType;
  onClick?: () => void;
};

function ListItem({ label, href, icon: Icon, onClick }: ListItemProps) {
  return (
    <li
      onClick={onClick}
      className="cursor-pointer leading-[18px] relative whitespace-nowrap hover:bg-gray-300 hover:transition-all hover:duration-100 hover:ease-out">
      <Link
        target="_blank"
        href={href}
        className="flex items-center py-[8px] px-[16px] w-full">
        <Icon className="w-6 h-[24px] text-blue mr-4" />
        <span>{label}</span>
      </Link>
    </li>
  );
}

export default function UserDropDown({ dict, children }: Props) {
  const [sessionId, setSessionId] = useState<string>('');
  const [dropDownIsOpen, setDropDownIsOpen] = useState<boolean>(false);
  const { userGrant, logout: contextLogout, user } = useUserContext();
  const { admin, owner, customer, logout } = dict;

  const { adminHrefs, ownerHrefs, customerHrefs } =
    dropdownPaths[user.languageCode || 'en'];

  const pathname = usePathname();

  const cookies = getCookies();

  const ref = useClickOutside({
    clickFn: () => setDropDownIsOpen(false),
  });

  async function handleLogout() {
    await contextLogout();
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  useEffect(() => {
    const sessionId = getCookie('sessionId');
    const sessionIdExists = hasCookie('sessionId');

    if (sessionIdExists && sessionId) {
      setSessionId(sessionId);
    }
  }, [cookies]);

  return (
    <div
      ref={ref}
      onClick={() => setDropDownIsOpen(!dropDownIsOpen)}
      className="w-10 h-[40px] relative whitespace-nowrap inline-flex items-center uppercase text-sm cursor-pointer">
      {children}
      <ul
        className={cn(
          'z-[999] w-auto absolute pt-[16px] px-[8px] pb-[12px] bg-white shadow-[0_1px_10px_0_rgba(0,0,0,.2)] top-[120%] right-0 min-w-[260px] list-none text-gray-900',
          {
            hidden: !dropDownIsOpen,
          },
        )}>
        {userGrant == 'admin' && (
          <ListItem
            href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
              adminHrefs.dashboardHref,
            )}`}
            label={admin.dashboard}
            icon={DashboardIcon}
          />
        )}
        {userGrant == 'owner' && (
          <>
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                ownerHrefs.dashboardHref,
              )}`}
              label={owner.dashboard}
              icon={DashboardIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                ownerHrefs.bookingsHref,
              )}`}
              label={owner.bookings}
              icon={CalendarBookingsIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                ownerHrefs.feedbackHref,
              )}`}
              label={owner.feedback}
              icon={DialogBoxIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                ownerHrefs.yourPaymentsHref,
              )}`}
              label={owner.yourPayments}
              icon={WalletIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                ownerHrefs.travelDocumentsHref,
              )}`}
              label={owner.travelDocuments}
              icon={DocumentsIcon}
            />
          </>
        )}
        {userGrant == 'customer' && (
          <>
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.dashboardHref,
              )}`}
              label={customer.dashboard}
              icon={DashboardIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.villagesHref,
              )}`}
              label={customer.villages}
              icon={VillageIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.experiencesHref,
              )}`}
              label={customer.experiences}
              icon={BalloonIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.yourPaymentsHref,
              )}`}
              label={customer.yourPayments}
              icon={WalletIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.travelDocumentsHref,
              )}`}
              label={customer.travelDocuments}
              icon={DocumentsIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.planYourTripHref,
              )}`}
              label={customer.planYourTrip}
              icon={FlagIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customerHrefs.profileHref,
              )}`}
              label={customer.profile}
              icon={ProfileIcon}
            />
          </>
        )}
        <ListItem
          onClick={handleLogout}
          href={pathname}
          label={logout}
          icon={PowerIcon}
        />
      </ul>
    </div>
  );
}
